/**
 * Phone number formatting and validation utilities
 *
 * @example
 * ```ts
 * formatPhoneNumber('5551234567') // '(555) 123-4567'
 * validatePhoneNumber('555-123-4567') // { isValid: true }
 * validatePhoneNumber('555-123') // { isValid: false, error: 'Need 4 more digits' }
 * ```
 */

/**
 * Format a phone number as (XXX) XXX-XXXX
 * Works incrementally as user types
 */
export function formatPhoneNumber(value: string): string {
  // Strip non-digits
  const digits = value.replace(/\D/g, '');

  // Limit to 10 digits
  const limited = digits.slice(0, 10);

  // Format progressively
  if (limited.length === 0) return '';
  if (limited.length <= 3) return limited;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
}

/**
 * Extract just digits from a formatted phone number
 */
export function unformatPhoneNumber(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Validation result for phone numbers
 */
export interface PhoneValidationResult {
  isValid: boolean;
  error?: string;
  warning?: string;
}

/**
 * Invalid phone patterns to reject
 */
const INVALID_PATTERNS = [
  /^555\d{7}$/, // 555-XXX-XXXX (TV/movie test numbers)
  /^(\d)\1{9}$/, // All same digit (0000000000)
  /^0{3}/, // Starts with 000
  /^1{3}/, // Starts with 111
  /^123456789\d?$/, // Sequential
  /^(\d)\1{2}\1{3}\1{4}$/, // XXX-XXX-XXXX all same
];

/**
 * Area codes that don't exist (common fake numbers)
 */
const INVALID_AREA_CODES = [
  '000', '111', '222', '333', '444', '555', '666', '777', '888', '999',
  '123', '234', '345', '456', '567', '678', '789',
];

/**
 * Validate a phone number
 */
export function validatePhoneNumber(value: string): PhoneValidationResult {
  // Empty is valid (field is optional)
  if (!value || value.trim() === '') {
    return { isValid: true };
  }

  const digits = unformatPhoneNumber(value);

  // Check digit count
  if (digits.length === 0) {
    return { isValid: true }; // Empty after stripping
  }

  if (digits.length < 10) {
    return {
      isValid: false,
      error: `Need ${10 - digits.length} more digit${10 - digits.length > 1 ? 's' : ''}`,
    };
  }

  if (digits.length > 10) {
    return {
      isValid: false,
      error: 'Too many digits (expected 10)',
    };
  }

  // Check for invalid patterns
  for (const pattern of INVALID_PATTERNS) {
    if (pattern.test(digits)) {
      return {
        isValid: false,
        error: 'Invalid number pattern',
      };
    }
  }

  // Check area code
  const areaCode = digits.slice(0, 3);
  if (INVALID_AREA_CODES.includes(areaCode)) {
    return {
      isValid: false,
      error: `Invalid area code (${areaCode})`,
    };
  }

  // Warn about toll-free numbers (valid but might not be direct line)
  const tollFreeAreaCodes = ['800', '888', '877', '866', '855', '844', '833'];
  if (tollFreeAreaCodes.includes(areaCode)) {
    return {
      isValid: true,
      warning: 'Toll-free number (may not be direct line)',
    };
  }

  return { isValid: true };
}

/**
 * Check if a phone number looks complete enough to validate
 */
export function isPhoneComplete(value: string): boolean {
  const digits = unformatPhoneNumber(value);
  return digits.length >= 10;
}
